import type { TFunction } from 'i18next';

import { relativeTime } from '../../../settings/components/AccountSection/time';
import type { BlogCopy } from './blog-summary';

/** "Oct 4, 11:00 AM" in English: the shape the Blog page's Autopilot panel gives the next run. */
const NEXT_RUN_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

const nextRun = (at: string, locale: string | undefined): string => {
  const date = new Date(at);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString(locale, NEXT_RUN_FORMAT);
};

/** "Jan 15, 2025" in English: the date `relativeTime` falls back to for anything a month old. */
const OLD_DATE_FORMAT: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

/** Whole days since `ms`, rounded step by step as `relativeTime` rounds them, so the card switches where it does. */
const daysAgo = (ms: number): number =>
  Math.round(Math.round(Math.round(Math.round((Date.now() - ms) / 1000) / 60) / 60) / 24);

/** The app's relative time, except that a month-old date is written in `locale`, not in the browser's. */
const lastArticle = (t: TFunction, at: string, locale: string | undefined): string => {
  const ms = new Date(at).getTime();
  if (Number.isNaN(ms) || daysAgo(ms) < 30) return relativeTime(t, at);
  return new Date(ms).toLocaleDateString(locale, OLD_DATE_FORMAT);
};

/**
 * The Blog card's copy: one literal `t()` key per id, so the extractor harvests every one; the `switch` is exhaustive,
 * so a new id fails the typecheck until it has its copy. Dates are written in the owner's language: the next run
 * through `Intl` with `locale` (the app language as a BCP-47 tag, `dateLocale`), the last article's age through the
 * app's translated relative time (and, a month on, its date through `Intl` with `locale`). Not moment: its global locale is whichever locale file the app loaded last
 * (Portuguese today), whatever language the owner reads.
 */
export const blogCopy = (t: TFunction, c: BlogCopy, locale?: string): string => {
  switch (c.id) {
    case 'needs-credits':
      return t('Needs credits');
    case 'off':
      return t('Off');
    case 'writing':
      return c.n === 1 ? t('Writing 1 article') : t('Writing {{n}} articles', { n: c.n });
    case 'autopilot-needs-credits':
      return t('Autopilot needs credits');
    case 'drafts-to-finish':
      return c.n === 1 ? t('1 draft to finish') : t('{{n}} drafts to finish', { n: c.n });
    case 'blog-off':
      return t('Your blog is off');
    case 'autopilot-next':
      return t('Autopilot on, next article {{when}}', { when: nextRun(c.at, locale) });
    case 'last-article':
      return t('Last article {{when}}', { when: lastArticle(t, c.at, locale) });
    case 'write-first-article':
      return t('Write your first article');
    case 'blog-unavailable':
      return t("Your blog isn't available yet");
    case 'buy-credits':
      return t('Buy credits');
    case 'continue-draft':
      return t('Continue draft');
    case 'new-article':
      return t('New article');
    case 'view-articles':
      return t('View articles');
    case 'open-blog':
      return t('Open blog');
  }
};
